// IJCEPACertificate.jsx
import React from "react";

export default function IJCEPACertificate() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            IJCEPA Certificate
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            IJCEPA Certificate for India-Japan Exports
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks supports exporters in obtaining IJCEPA
            Certificates to avail preferential tariff benefits under the
            India-Japan Comprehensive Economic Partnership Agreement.
          </p>
          <p className="text-gray-700">
            The process involves precise documentation, origin verification, and
            application submission through DGFT's digital platform to ensure
            eligibility for reduced tariffs and duty concessions in Japan.
          </p>
        </div>

        {/* Right: Image (IJCEPA CERTIFICATE - India-Japan trade documents, export certification, international shipping) */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/3943721/pexels-photo-3943721.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Exporter obtaining IJCEPA Certificate"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* PROFESSIONAL SUPPORT & DOCUMENTS SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Professional compliance support */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks provides specialized assistance for IJCEPA
              Certificate applications, ensuring exporters qualify for Japan
              market tariff preferences under the Comprehensive Economic
              Partnership Agreement.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Verification of product eligibility under IJCEPA rules of
                  origin and HS code tariff concession schedules.
                </li>
                <li>
                  Complete Certificate of Origin preparation through DGFT's COO
                  digital platform with accurate preference criteria declaration.
                </li>
                <li>
                  Coordination with Export Inspection Council (EIC) or authorized
                  agencies for certification and document validation.
                </li>
                <li>
                  Compliance advisory on consignment criteria and third-country
                  invoicing provisions to maintain certificate validity.
                </li>
              </ul>
            </div>
          </div>

          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for IJCEPA Certificate
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks assists in compiling shipment-specific
              documentation required for IJCEPA preferential tariff certification.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>Valid Import Export Code (IEC) certificate of the exporter.</li>
                <li>
                  Commercial export invoice and packing list with detailed product
                  description, HS codes, and quantity/weight specifications.
                </li>
                <li>
                  Detailed product description including manufacturing process,
                  raw material sources, and origin criteria compliance (Wholly
                  Obtained or Substantial Transformation).
                </li>
                <li>
                  Supporting documents proving origin such as bill of materials,
                  supplier declarations, or manufacturing process flow charts.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm offers specialized international trade compliance expertise
              for IJCEPA certification and Japan market access requirements.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  International Trade Compliance Expertise
                </h3>
                <p className="text-gray-700 text-sm">
                  Comprehensive knowledge of IJCEPA rules of origin, preference
                  criteria, and Japan customs tariff concession schedules.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Accurate Documentation Support
                </h3>
                <p className="text-gray-700 text-sm">
                  Precise Certificate of Origin preparation meeting EIC/DGFT
                  standards with correct HS classification and origin declarations.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Reliable Advisory Service
                </h3>
                <p className="text-gray-700 text-sm">
                  End-to-end support from eligibility verification through
                  certification issuance and Japan customs compliance guidance.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Access Japan Market Tariff Benefits
              </h3>
              <p className="text-gray-700 mb-5">
                Connect with Hawkins &amp; Brooks to obtain your IJCEPA
                Certificate and unlock preferential tariffs for your exports to
                Japan under the Comprehensive Economic Partnership Agreement.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
                >
                  Get Consultation
                </a>
                <a
                  href="tel:+919217468906"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
                >
                  Call Hawkins &amp; Brooks
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
